import { motion } from 'framer-motion'
import { ArrowRight, ExternalLink } from 'lucide-react'
import { useLanguage } from '../../context/LanguageContext'
import { Link } from 'react-router-dom'
import { useState } from 'react'

const Portfolio = () => {
  const { t } = useLanguage()
  const [hoveredIndex, setHoveredIndex] = useState(null)

  const projects = [
    {
      title: 'AURELIA Restaurant',
      category: 'Web Development',
      image: '/portfolio/aurelia.jpg',
      description: 'Luxury restaurant website with premium gold design',
      link: null,
      status: 'Coming Soon'
    },
    {
      title: 'Portfolio CV Website',
      category: 'Web Development',
      image: '/portfolio/portfolio-cv.jpg',
      description: 'Modern portfolio website with animated network background',
      link: 'https://kedjon-portfolio.vercel.app',
      status: 'Live'
    }
  ]

  return (
    <section className="py-24 bg-gradient-to-br from-bronze-950 to-dark relative overflow-hidden">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="section-title">
            {t('portfolio.title')} <span className="text-gradient-gold">{t('portfolio.titleHighlight')}</span>
          </h2>
          <p className="text-xl text-beige-200 mt-4 max-w-2xl mx-auto">
            {t('portfolio.subtitle')}
          </p>
        </motion.div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{ 
                delay: index * 0.1,
                type: 'spring',
                stiffness: 100
              }}
              onHoverStart={() => setHoveredIndex(index)}
              onHoverEnd={() => setHoveredIndex(null)}
              whileHover={{ y: -15, transition: { duration: 0.3 } }}
              onClick={() => project.link && window.open(project.link, '_blank')}
              className="group relative overflow-hidden rounded-2xl shadow-elegant hover:shadow-gold-lg transition-all duration-500 cursor-pointer"
            >
              {/* Image */}
              <div className="aspect-[4/3] relative overflow-hidden bg-dark">
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark/80 via-dark/40 to-transparent"></div>
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
                {/* Overlay */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: hoveredIndex === index ? 1 : 0 }}
                  className="absolute inset-0 bg-gradient-to-t from-dark via-dark/80 to-transparent flex flex-col justify-end p-6"
                >
                  <span className="text-gold-400 text-sm font-semibold mb-2">
                    {project.category}
                  </span>
                  <h3 className="text-2xl font-display font-bold text-white mb-2">
                    {project.title}
                  </h3>
                  <p className="text-beige-200 mb-4">
                    {project.description}
                  </p>
                  {project.link ? (
                    <button className="flex items-center space-x-2 text-gold-400 font-semibold group-hover:text-white transition-colors">
                      <span>{t('portfolio.viewProject')}</span>
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  ) : (
                    <span className="inline-block px-3 py-1 bg-bronze-900/50 text-gold-400 text-sm rounded-full">
                      {project.status}
                    </span>
                  )}
                </motion.div>

                {/* Gradient Overlay on Hover */}
                <motion.div
                  initial={{ scale: 1 }}
                  animate={{ scale: hoveredIndex === index ? 1.1 : 1 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(184, 134, 11, 0.1) 100%)`,
                  }}
                ></motion.div>
              </div>

              {/* Card Footer */}
              <motion.div
                initial={{ y: 0 }}
                animate={{ y: hoveredIndex === index ? -10 : 0 }}
                className="absolute bottom-0 left-0 right-0 p-6 bg-dark/95 backdrop-blur-sm border-t border-gold-500/20"
              >
                <span className="text-gold-400 text-sm font-semibold">
                  {project.category}
                </span>
                <h3 className="text-xl font-display font-bold text-cream mt-1">
                  {project.title}
                </h3>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link to="/portfolio" className="btn-primary inline-flex items-center space-x-2">
            <span>{t('portfolio.viewAll')}</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}

export default Portfolio
